import {logger} from 'yougile-util'
import {DataObjectId, Hub} from 'yougile-model'
import {DirectMessages} from '../client/direct-messages'
import {EventBus} from '../client/event-bus'
import * as events from '../client/events'
import {Sync} from '../client/sync'
import {SyncEvents} from '../client/sync-events'
import {Board} from './board'
import {ChatUtils} from './chat-utils'
import {globals} from './globals-base'
import {Project} from './project'

type MailboxItemType =
  | 'task-chat' // new messages in task chat
  | 'personal-chat' // new messages in personal chat
  | 'task' // new tasks in project

export interface MailboxItem {
  type: MailboxItemType
  count: number
  target: Hub
  isNew: boolean
  timestamp: number
}

/**
 * Notifications mailbox for user.
 * Contains notifications such as new messages in chats, new tasks in projects.
 * After calling init() it fills mailbox with unread tasks and projects
 * and checks for any updates.
 */
export class Mailbox {
  private companyId: DataObjectId
  private items: Map<DataObjectId, MailboxItem> = new Map()

  constructor(companyId: DataObjectId) {
    this.companyId = companyId
  }

  public getCompanyId(): DataObjectId {
    return this.companyId
  }

  public list(): MailboxItem[] {
    return [...this.items.values()].filter(i => i.isNew)
  }

  public remove(id: DataObjectId): void {
    this.items.delete(id)
  }

  public clearAllOld(): void {
    ;[...this.items.values()].forEach(item => {
      if (!item.isNew) {
        this.remove(item.target.id)
      }
    })
  }

  public update(): boolean {
    if (!Sync.get({companyId: this.companyId})) {
      return false
    }
    const box = Sync.get({companyId: this.companyId})
    if (!box && this.items.size > 0) {
      this.items.clear()
      return true
    }
    let changed = false
    const tasks = ChatUtils.tasksGroupedByUserPresence(box).withCurrentUser

    const upItem = (item: MailboxItem): void => {
      if (!item.target) {
        return
      }
      const old = this.items.get(item.target.id)
      if (item.isNew) {
        if (
          !old ||
          !old.isNew ||
          old.count !== item.count ||
          old.timestamp !== item.timestamp
        ) {
          this.items.set(item.target.id, item)
          changed = true
        }
      } else {
        if (old && old.isNew) {
          old.isNew = false
          changed = true
        }
      }
    }
    const processedIds: Set<DataObjectId> = new Set()

    if (this.companyId === Sync.profileId) {
      Sync.getOtherUserIds().forEach(uid => {
        const count = ChatUtils.newMessagesFromPersonalChat({id: uid})
        const target = DirectMessages.getChatIfExists(uid)
        if (target) {
          processedIds.add(target.id)
        }
        upItem({
          timestamp: count > 0 ? target.timestamp : 0,
          isNew: count > 0,
          type: 'personal-chat',
          count,
          target
        })
      })
    } else {
      tasks.forEach(t => {
        const count = ChatUtils.newMessagesFromChat(t)
        if (t) {
          processedIds.add(t.id)
        }
        upItem({
          timestamp: count > 0 ? t.chat.lastMessage().timestamp : 0,
          isNew: count > 0,
          type: 'task-chat',
          count,
          target: t
        })
      })
      Project.listProjects(this.companyId, 'not-archive', true).forEach(p =>
        Project.listBoards(p).forEach(b => {
          const newTasks = Board.getNewTasks(b)
          if (b) {
            processedIds.add(b.id)
          }
          upItem({
            timestamp: newTasks.reduce(
              (time, task) => Math.max(time, task.timestamp),
              0
            ),
            isNew: newTasks.length > 0,
            type: 'task',
            count: newTasks.length,
            target: b
          })
        })
      )
    }

    ;[...this.items].forEach(([id, item]) => {
      if (!processedIds.has(item.target.id)) {
        this.items.delete(id)
        changed = true
      }
    })

    return changed
  }
}

export class MailboxManager {
  private static instances: Map<DataObjectId, Mailbox> = new Map()

  public static get(companyId: DataObjectId): Mailbox {
    return MailboxManager.instances.get(companyId)
  }

  public static init(): void {
    Sync.listSandboxes().forEach(s => {
      if (MailboxManager.instances.has(s.companyId)) {
        return
      }
      const m = new Mailbox(s.companyId)
      MailboxManager.instances.set(s.companyId, m)
      MailboxManager.sync(m)
    })
    SyncEvents.addNewCompanyListener(companyId => {
      const box = Sync.get({companyId})
      const {instances} = MailboxManager
      if (box && !instances.has(companyId)) {
        const m = new Mailbox(companyId)
        instances.set(companyId, m)
        MailboxManager.sync(m)
      }
    })
  }

  private static sync(mailbox: Mailbox): void {
    let shouldUpdate = true
    const companyId = mailbox.getCompanyId()
    SyncEvents.addListener(companyId, '*', () => (shouldUpdate = true))
    setInterval(() => {
      if (globals.getCompanyId() && shouldUpdate) {
        shouldUpdate = false
        try {
          const changed = mailbox.update()
          if (changed) {
            EventBus.dispatch(events.mailboxUpdate(companyId))
          }
        } catch (e) {
          logger.error(e)
        }
      }
    }, 20_000)
  }
}
